/*
 * DIFF vs V1 — wrapper local remplaçant le UIChart de PrimeNG 8 ('primeng/chart').
 *
 * Pourquoi : le ChartModule de PrimeNG 21 exige Chart.js 4, incompatible avec le rendu
 * épinglé de la migration (Chart.js 2.9.4, chargé en SCRIPT GLOBAL → window.Chart,
 * cf. angular.json "scripts"). Ce composant réimplémente à l'identique l'API publique
 * du p-chart v8 (mêmes @Input/@Output, mêmes méthodes publiques refresh()/reinit()/
 * getBase64Image()/generateLegend(), même template/DOM) mais instancie le graphe via
 * `new (window as any).Chart(canvas, { type, data, options, plugins })`.
 *
 * Comportement conservé du v8 :
 *  - setters data/options → destroy + recreate (reinit) si le graphe est déjà initialisé ;
 *  - responsive=true par défaut ; maintainAspectRatio forcé à false si width/height fournis ;
 *  - clic canvas → onDataSelect({originalEvent, element, dataset}) via getElementAtEvent /
 *    getDatasetAtEvent (API Chart.js 2.x, disparue en 4.x — d'où le wrapper).
 */
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output
} from '@angular/core';

@Component({
  selector: 'p-chart',
  template: `
    <div style="position:relative" [style.width]="responsive && !width ? null : width" [style.height]="responsive && !height ? null : height">
      <canvas [attr.width]="responsive && !width ? null : width" [attr.height]="responsive && !height ? null : height" (click)="onCanvasClick($event)"></canvas>
    </div>
  `,
  standalone: false
})
export class UiChartComponent implements AfterViewInit, OnDestroy {

  @Input() type: string;

  @Input() plugins: any[] = [];

  @Input() width: string;

  @Input() height: string;

  @Input() responsive: boolean = true;

  @Output() onDataSelect: EventEmitter<any> = new EventEmitter();

  initialized: boolean;

  _data: any;

  _options: any = {};

  chart: any;

  constructor(public el: ElementRef) {}

  @Input() get data(): any {
    return this._data;
  }

  set data(val: any) {
    this._data = val;
    this.reinit();
  }

  @Input() get options(): any {
    return this._options;
  }

  set options(val: any) {
    this._options = val;
    this.reinit();
  }

  ngAfterViewInit() {
    this.initChart();
    this.initialized = true;
  }

  onCanvasClick(event: any) {
    if (this.chart) {
      const element = this.chart.getElementAtEvent(event);
      const dataset = this.chart.getDatasetAtEvent(event);
      if (element && element[0] && dataset) {
        this.onDataSelect.emit({ originalEvent: event, element: element[0], dataset: dataset });
      }
    }
  }

  initChart() {
    const opts = this.options || {};
    opts.responsive = this.responsive;

    // allows chart to resize in responsive mode
    if (opts.responsive && (this.height || this.width)) {
      opts.maintainAspectRatio = false;
    }

    // Chart.js 2.9.4 global (window.Chart), chargé via angular.json "scripts".
    this.chart = new (window as any).Chart(this.el.nativeElement.children[0].children[0], {
      type: this.type,
      data: this.data,
      options: this.options,
      plugins: this.plugins
    });
  }

  getCanvas() {
    return this.el.nativeElement.children[0].children[0];
  }

  getBase64Image() {
    return this.chart.toBase64Image();
  }

  generateLegend() {
    if (this.chart) {
      return this.chart.generateLegend();
    }
  }

  refresh() {
    if (this.chart) {
      this.chart.update();
    }
  }

  reinit() {
    if (this.chart) {
      this.chart.destroy();
      this.initChart();
    }
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.initialized = false;
      this.chart = null;
    }
  }
}
